import React from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseRounded from "@mui/icons-material/CloseRounded";

import { useAuthState } from "../app/AuthStateContext";
import AuthOptions from "../auth/AuthOptions";
import {
  DEFAULT_DISCORD_INVITE_URL,
  fetchPublicDiscordInviteUrl,
} from "../public-site/discordInviteUrl";
import { colors } from "../templates/dark-hero/colors";
import { formatNightSentenceLabel } from "./cetTime";

const STEAM_VISIBILITY_NOTICE =
  "To play, your Steam profile must be public with your CS2 hours visible. We check when you join the match night.";
const VETTING_LINE =
  "New signups are reviewed by a real person, usually within a day. Sign up the day before to be confirmed in time.";
const CONFIRMATION_LINE_2 =
  "Make sure your Steam profile is public with CS2 hours visible before you join.";
const DISCORD_LINE =
  "Grab your match night role in Discord so we can ping you before it starts.";

const dialogPaperSx = {
  backgroundColor: colors.background.card,
  color: colors.text.primary,
  border: `1px solid ${colors.border.default}`,
  borderRadius: "16px",
};

const noticeSx = {
  mt: 2,
  p: "12px 14px",
  borderRadius: "10px",
  backgroundColor: "rgba(232,163,61,0.08)",
  border: `1px solid ${colors.border.default}`,
  fontSize: "0.8rem",
  lineHeight: 1.55,
  color: colors.text.secondary,
};

export function shouldNotifyMatchNightList(data) {
  return Boolean(data?.ok && data?.night?.id);
}

export default function MatchNightRsvpDialog({
  open,
  night = null,
  basePath,
  onClose,
  onRsvpSuccess = () => {},
}) {
  const { isAuthenticated } = useAuthState();
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [discordUrl, setDiscordUrl] = React.useState(DEFAULT_DISCORD_INVITE_URL);

  React.useEffect(() => {
    if (!open) {
      setResult(null);
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open || !isAuthenticated) return;
    let cancelled = false;
    setSubmitting(true);
    setError(null);

    fetch(`${basePath}/api/v1/match-nights/${night.id}/rsvp`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data?.ok) {
          setResult(data);
          if (shouldNotifyMatchNightList(data)) {
            onRsvpSuccess(data);
          }
        } else {
          setError(data?.message || "Something went wrong. Try again.");
        }
      })
      .catch(() => {
        if (!cancelled) setError("Something went wrong. Try again.");
      })
      .finally(() => {
        if (!cancelled) setSubmitting(false);
      });

    return () => {
      cancelled = true;
    };
    // Intentionally runs once per dialog open for this night; re-opening
    // remounts the effect via `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated, night?.id, basePath]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchPublicDiscordInviteUrl(basePath)
      .then((url) => {
        if (!cancelled) setDiscordUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, basePath]);

  if (!night) return null;

  const nightLabel = formatNightSentenceLabel(new Date(night.scheduledAt), night.region);
  const rsvpStatus = result?.rsvp?.status;
  const isWaitlisted = rsvpStatus === "waitlisted";
  const isConfirmedOrWaitlisted = rsvpStatus === "confirmed" || isWaitlisted;
  const isReassigned = Boolean(result?.reassigned);
  const isPendingReview = rsvpStatus === "pending_verification" && !isReassigned;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", top: 8, right: 8, color: colors.text.muted }}
      >
        <CloseRounded />
      </IconButton>

      <DialogTitle sx={{ color: colors.text.primary, fontWeight: 700 }}>
        {isAuthenticated ? "Reserve your spot" : "Sign in to reserve your spot"}
      </DialogTitle>

      <DialogContent>
        {!isAuthenticated && (
          <>
            <Typography sx={{ color: colors.text.secondary, fontSize: "0.9rem", mb: 2 }}>
              Sign in to join {nightLabel}.
            </Typography>
            <AuthOptions surface="match_night" />
            <Box sx={noticeSx}>{STEAM_VISIBILITY_NOTICE}</Box>
          </>
        )}

        {isAuthenticated && submitting && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress sx={{ color: colors.primary.main }} />
          </Box>
        )}

        {isAuthenticated && !submitting && error && (
          <Typography sx={{ color: colors.state.danger.main }}>{error}</Typography>
        )}

        {isAuthenticated && !submitting && !error && isConfirmedOrWaitlisted && (
          <Box>
            <Typography sx={{ color: colors.text.primary, fontWeight: 600, mb: 1 }}>
              {isWaitlisted
                ? `You're on the waitlist for ${nightLabel}. We'll confirm you if a spot opens up.`
                : `You're in for ${nightLabel}.`}
            </Typography>
            <Typography sx={{ color: colors.text.secondary, fontSize: "0.9rem", mb: 2 }}>
              {CONFIRMATION_LINE_2}
            </Typography>
            <Typography sx={{ color: colors.text.secondary, fontSize: "0.9rem", mb: 2 }}>
              {DISCORD_LINE}
            </Typography>
            <Button
              component="a"
              href={discordUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: colors.border.default,
                color: colors.text.primary,
              }}
            >
              Join the Discord
            </Button>
          </Box>
        )}

        {isAuthenticated && !submitting && !error && isReassigned && (
          <Box>
            <Typography sx={{ color: colors.text.primary, fontWeight: 600, mb: 1 }}>
              {result.message}
            </Typography>
            <Box sx={noticeSx}>{STEAM_VISIBILITY_NOTICE}</Box>
          </Box>
        )}

        {isAuthenticated && !submitting && !error && isPendingReview && (
          <Box>
            <Typography sx={{ color: colors.text.primary, fontWeight: 600, mb: 1 }}>
              You're reserved for {nightLabel}, pending verification.
            </Typography>
            <Typography sx={{ color: colors.text.secondary, fontSize: "0.9rem", mb: 2 }}>
              {VETTING_LINE}
            </Typography>
            <Box sx={noticeSx}>{STEAM_VISIBILITY_NOTICE}</Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

MatchNightRsvpDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  night: PropTypes.object,
  basePath: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onRsvpSuccess: PropTypes.func,
};
